import {initTeamsProvider} from '@ey/canvascoreservices-teams';
import {licenseSetter} from '@ey/canvascoreservices/AGGridEnterprise';
import {MsalService} from '@ey/voltron-core';
import {Providers, ProviderState} from '@microsoft/mgt';
import {LicenseManager} from 'ag-grid-enterprise';
import queryString from 'query-string';
import {getDocumentByDocumentId} from './actions/Ares/taskDocumentActions';
import {setDocumentId} from './actions/Document/documentactions';
import {getAllDocumentTypes} from './actions/Document/documenttypeactions';
import {
	getEngagement,
	getEngagementRoles,
	setEngagementId,
	setHasBodyIdFilter,
	setIsAresView,
	setIsDrawerMode,
	setIsGuidedWorkFlowV2,
	setTheme
} from './actions/Engagement/engagementactions';
import {getQuickLinks} from './actions/Engagement/quicklinksactions';
import {raiseInformationMessage, raiseJSValidationError} from './actions/erroractions';
import {createUserIndependenceForm} from './actions/FormData/formdataactions';
import {getActiveAndGuestFormUsers} from './actions/FormUsers/formusersactions';
import {getMateriality} from './actions/Materiality/materialityactions';
import {setApprovalsFromDocument} from './actions/Task/signoffsaction';
import {getInternalUserById, getUserPreferences} from './actions/UserPreferences/userpreferencesaction';
import {getConfigurations} from './actions/Configuration/configurationactions';
import {setSelectedStEntityId} from './actions/MultiEntities/stEntitiesactions';
import initDocHelper from './components/Common/DocumentHub';
import {expandableGetFormById} from './util/Ares/customHooks';
import {checkIsMFE} from './util/customHooks';
import env from './util/env';
import {
	attributeListSkipTaskOpen,
	displayLanguages,
	documentsApiExpandableItems,
	guidEmpty,
	IndependenceAndProfileDocumentTypes,
	serviceURL
} from './util/uiconstants';
import Utility from './util/utilityfunctions';
import {isNullOrUndefined, labels, setLanguage} from './util/utils';
import {getBaseMaterialityService} from './services/Materiality/materialityService';

export function initialize({store, config, path, basename, preloadedState}) {
	// Fallback to current window.location if path is undefined to avoid invalid URL (e.g., "undefined")
	let currentURL = path ? new URL(path, window.location.origin) : window.location;
	const theme = preloadedState?.theme;
	const isFullscreenForm = preloadedState?.isFullscreenForm;
	const isDrawer = preloadedState?.isDrawer;

	// Set language override from host in MF mode
	if (preloadedState?.userPreferences?.displayLanguage) {
		env.setLanguage(preloadedState.userPreferences.displayLanguage);
	}

	if (path) {
		Utility.setLocationParentObject({
			location: currentURL,
			isMFE: true
		});
	} else if (basename) {
		Utility.setLocationParentObject({
			location: currentURL,
			basename: basename,
			isMFE: true
		});
	}

	let authService = new MsalService(config.ad);
	let pathname = currentURL.pathname.toLowerCase();

	initializeTeamsIntegration(authService, config.ad);
	let engagementId = initializeEngagementId(store);
	initializeDocumentId(store);
	let {isFitRedirectionUrl} = initializeQueryParameters(store, path);
	initializeLinks();

	if (pathname === '/initProfile'.toLowerCase()) {
		initializeProfileForm(store, engagementId);
	} else if (pathname === '/independence'.toLowerCase()) {
		initializeIndependenceForm(store, engagementId);
	} else {
		initializeGuidedWorkflow(store, authService, engagementId, isFitRedirectionUrl, theme, isFullscreenForm, isDrawer);
	}

	initializeReturnURL();
	initializeLicensing();
}

export function initializeApp(store, isFitRedirecionUrl, isFullscreenForm, isDrawer) {
	const state = store.getState();
	const dispatch = store.dispatch;
	const engagement = state.engagement;
	const isAresView = state.isAresView;
	const isEngagementAccessible = state.userPreferences.engagementUserIsEngagementAccessible;

	/**
	 * Note: whenever dev adds code here that impact the initial loading of document or engagement setup,
	 * pls make sure to update the useDocumentSwitcher customHook (util/customHook) as well.
	 */
	let documentId = Utility.getDocumentIdFromUrl(window);
	if (documentId) {
		const getDocumentByIdParameters = {
			expand: documentsApiExpandableItems.tasks,
			includeApprovals: true,
			includeReviewNotes: true
		};

		getDocumentByIdParameters.includeRelatedEntities = true;

		let stEntityId;
		let getDocumentById = new Promise((resolve) => {
			dispatch(getDocumentByDocumentId(engagement.engagementId, documentId, getDocumentByIdParameters, isAresView)).then((doc) => {
				if (doc.entities?.length > 0) {
					stEntityId = doc.entities[0].id;
				}

				if (engagement.isMultiEntityEngagement && stEntityId) {
					// Update stEntityId Redux store
					dispatch(setSelectedStEntityId(stEntityId));

					// Update URL without page reload
					const currentUrl = new URL(window.location.href);
					currentUrl.searchParams.set('stentityid', stEntityId);
					window.history.replaceState(null, '', currentUrl.toString());
				}

				if (isEngagementAccessible && !IndependenceAndProfileDocumentTypes.includes(doc.documentTypeId)) {
					const materialityService = getBaseMaterialityService(engagement.engagementId, documentId, doc, engagement.isMultiEntityEngagement);

					stEntityId = materialityService.getSelectedStEntityId();

					const queryParameters = {stEntityId: stEntityId};

					dispatch(getMateriality(engagement.engagementId, undefined, queryParameters));
				}
				return resolve(doc);
			});
		});

		let getFormById = new Promise((resolve) => {
			dispatch(expandableGetFormById(engagement, documentId, undefined, {resolve: true}, undefined, isAresView)).then((form) => resolve(form));
		});

		// Get doc byId call needs to be fired before the form byId. Otherwise, fit redirection won't work with MEST engagements
		let documentMetadataPromise = Promise.resolve();
		if (isFitRedirecionUrl) {
			documentMetadataPromise = getDocumentById.then((docResponse) => {
				return getFormById.then((formResponse) => [docResponse, formResponse]);
			});
		} else {
			// It's not a fit redirection url. Both calls can go in parallel
			documentMetadataPromise = Promise.all([getDocumentById, getFormById]);
		}

		documentMetadataPromise
			.then((responses) => {
				let document = responses[0];
				if (document && document.approvals && document.approvals.length > 0) {
					return dispatch(setApprovalsFromDocument(document.approvals, document.id));
				}

				return Promise.resolve();
			})
			.catch(() => {
				store.dispatch(raiseJSValidationError(null, labels.invalidDocumentId, checkIsMFE(), false));
			});

		dispatch(getActiveAndGuestFormUsers(Number(engagement.engagementId), documentId));

		dispatch(getAllDocumentTypes());

		if (!isFullscreenForm && isEngagementAccessible) {
			dispatch(getQuickLinks(engagement.engagementId));
		}
	}
}

export function initializeDocHelper(store, authService) {
	/**
	 * sending UPN using msal logged in account so that docHelper can be initialitzed based on UPN route
	 * DocHelper init is chained to userpreference call so that any error on DocHelper can be displayed in preferred Language
	 */
	const state = store.getState();
	const dispatch = store.dispatch;

	env.setDocHelperConfig(authService.getActiveAccount()?.username).then(() => {
		initDocHelper(store)
			.then(() => {
				console.log('📃 ###Init Dochelper ###');
			})
			.catch((e) => {
				console.log('Error in docHelperInit: ', e);
			});
	});
}

export function initializeDocumentId({dispatch}) {
	let documentId = Utility.getDocumentIdFromUrl(window);

	if (documentId) {
		dispatch(setDocumentId(documentId)).catch(() => {
			dispatch(raiseJSValidationError(null, labels.invalidDocumentId, checkIsMFE(), false));
		});
	}

	return documentId;
}

export function initializeLicensing() {
	LicenseManager.setLicenseKey(
		// eslint-disable-next-line max-len
		'Using_this_{AG_Grid}_Enterprise_key_{AG-082593}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Ernst_&_Young_U.S._LLP}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{EY_Canvas}_only_for_{5}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{EY_Canvas}_need_to_be_licensed___{EY_Canvas}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{28_July_2026}____[v3]_[01]_MTc4NTE5MzIwMDAwMA==92613368de56a13fe2c327993abb09a5'
	);

	licenseSetter(
		// eslint-disable-next-line max-len
		'Using_this_{AG_Grid}_Enterprise_key_{AG-082593}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Ernst_&_Young_U.S._LLP}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{EY_Canvas}_only_for_{5}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{EY_Canvas}_need_to_be_licensed___{EY_Canvas}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{28_July_2026}____[v3]_[01]_MTc4NTE5MzIwMDAwMA==92613368de56a13fe2c327993abb09a5'
	);
}

export function initializeQueryParameters(store, path) {
	const dispatch = store.dispatch;
	const currentPath = path ? new URL(path, window.location.origin) : Utility.getLocation();

	const pathname = currentPath.pathname.toLowerCase();
	const queryParameters = queryString.parse(currentPath.search.toLowerCase());

	const hasBodyIdFilter = queryParameters?.bodyid ? true : false;
	dispatch(setHasBodyIdFilter(hasBodyIdFilter));

	const isGuidedWorkFlowV2 = pathname.indexOf('/guidedworkflowv2') === 0 || pathname.indexOf('/forms/guidedworkflowv2') === 0;

	const isGuidedWorkFlowV1 = pathname.indexOf('/guidedworkflow') === 0;

	dispatch(setIsGuidedWorkFlowV2(isGuidedWorkFlowV2));

	const isDrawerMode = queryParameters?.isdrawer && queryParameters.isdrawer.toLowerCase() === 'true';
	dispatch(setIsDrawerMode(isDrawerMode));

	const isFitRedirectionUrl = pathname.startsWith('/fit') || pathname.startsWith('/forms/fit');

	if (isFitRedirectionUrl || isGuidedWorkFlowV2 || isGuidedWorkFlowV1) {
		dispatch(setIsAresView(true));
	}

	return {isFitRedirectionUrl};
}

export function initializeEngagementId({dispatch}) {
	let engagementId = Utility.getEngagementIdFromUrl(window);

	if (engagementId && engagementId instanceof Array) {
		engagementId = engagementId[0];
	}

	dispatch(setEngagementId(engagementId)).catch(() => {
		dispatch(raiseJSValidationError(null, labels.invalidEngagementId, checkIsMFE(), false));
	});

	return engagementId;
}

export function initiateEngagement(store, engagementId, authService) {
	const dispatch = store.dispatch;

	return dispatch(getUserPreferences(`${env.getBaseURL(serviceURL, Number(engagementId))}Users/Me`))
		.catch((e) => {
			console.log('Error in getUserPreferences call: ', e);
			dispatch(getEngagementRoles(`${env.getBaseURL(serviceURL)}Roles`));
		})
		.then(async () => {
			let state = store.getState();
			let promise = Promise.resolve();
			await setLanguage(state.userPreferences.displayLanguage, state.userPreferences.subServiceLineId, state.userPreferences.languageResource);

			// Initialize configuration features after user preferences are loaded.
			initializeConfigurationFeatures(store);

			if (!state.userPreferences.isGuestUser) {
				initializeDocHelper(store, authService);
				promise = () => dispatch(getInternalUserById(Number(engagementId), state.userPreferences.id, true));
				return promise().then(() => {
					state = store.getState();
					let isEngagementAccessible = !state.userPreferences.isGuestUser && state.internalUser?.independenceInformation?.isEngagementAccessible;

					return dispatch(getEngagement(engagementId, isEngagementAccessible)).catch((result) => {
						if (result && result.response && result.response.status === 403) {
							dispatch(raiseJSValidationError(null, labels.invalidEngagementId, checkIsMFE(), false));
						}
					});
				});
			} else {
				state = store.getState();
				let isEngagementAccessible = !state.userPreferences.isGuestUser && state.internalUser?.independenceInformation?.isEngagementAccessible;

				return dispatch(getEngagement(engagementId, isEngagementAccessible)).catch((result) => {
					if (result && result.response && result.response.status === 403) {
						dispatch(raiseJSValidationError(null, labels.invalidEngagementId, checkIsMFE(), false));
					}
				});
			}
		});
}

export function initializeGuidedWorkflow(store, authService, engagementId, isFitRedirectionUrl, theme, isFullscreenForm, isDrawer) {
	const dispatch = store.dispatch;
	dispatch(setTheme(theme));
	return initiateEngagement(store, engagementId, authService).then(async () => {
		let state = store.getState();
		initializeApp(store, isFitRedirectionUrl, isFullscreenForm, isDrawer);
		await setLanguage(state.userPreferences.displayLanguage, state.userPreferences.subServiceLineId, state.userPreferences.languageResource);
		dispatch(getEngagementRoles(`${env.getBaseURL(serviceURL)}Roles`));
	});
}

export function initializeIndependenceForm(store, engagementId) {
	const {dispatch} = store;
	const isMFE = checkIsMFE();

	/*Independence Form*/
	dispatch(raiseInformationMessage(null, labels.verifyingIndependence, 0, isMFE));

	/*get user for user id*/
	dispatch(getUserPreferences(`${env.getBaseURL(serviceURL, Number(engagementId))}Users/Me`))
		.then(async () => {
			await setLanguage(
				store.getState().userPreferences.displayLanguage,
				store.getState().userPreferences.subServiceLineId,
				store.getState().userPreferences.languageResource
			);

			dispatch(getInternalUserById(engagementId, store.getState().userPreferences.id, true))
				.then(() => {
					if (
						store.getState().internalUser &&
						store.getState().internalUser.independenceIndividualDocumentId &&
						store.getState().internalUser.independenceIndividualDocumentId !== guidEmpty
					) {
						window.location.href = `${new URL(Utility.getLocation().origin)}fit?engagementid=${engagementId}&documentid=${
							store.getState().internalUser.independenceIndividualDocumentId
						}`;
					} else {
						/*create an independence form*/
						let createFormUrl = env.getBaseURL(serviceURL, Number(engagementId)) + 'IndependenceForms';
						dispatch(raiseInformationMessage(null, labels.creatingIndependenceForm, 0, isMFE));

						dispatch(createUserIndependenceForm(createFormUrl))
							.then(() => {
								if (store.getState().independenceIndividualDocumentId) {
									window.location.href = `${new URL(window.location.origin)}fit?engagementid=${engagementId}&documentid=${
										store.getState().independenceIndividualDocumentId
									}`;
								} else {
									dispatch(raiseJSValidationError(null, labels.independenceFormCreationFailed, isMFE, false));
								}
							})
							.catch(() => {
								dispatch(raiseJSValidationError(null, labels.independenceFormCreationFailed, isMFE, false));
							});
					}
				})
				.catch(() => {
					dispatch(raiseJSValidationError(null, labels.getUserByIdFailed, isMFE, false));
				});
		})
		.catch(() => {
			dispatch(raiseJSValidationError(null, labels.meCallFailed, isMFE, false));
		});
}

export function initializeLinks() {
	document.addEventListener('contextmenu', (event) => {
		// Check if the right-click is on a link
		const link = event.target.closest('a');
		if (link) {
			link.href = Utility.removeQueryStringProperties(link.href, ['isdrawer', 'framekey', 'target']);
		}
	});
}

export function initializeConfigurationFeatures(store) {
	const dispatch = store.dispatch;
	const configurationUrl = env.getBaseURL(serviceURL) + 'FeatureConfigurations';

	dispatch(getConfigurations(configurationUrl));
}

export function initializeProfileForm(store, engagementId) {
	const {dispatch} = store;
	const isMFE = checkIsMFE();

	/*Profile V2 form*/
	dispatch(raiseInformationMessage(null, labels.gettingProfile, 0, isMFE));

	dispatch(getQuickLinks(engagementId))
		.then(() => {
			if (store.getState().quickLink) {
				const quicklink = store.getState().quickLink;
				if (quicklink) {
					if (quicklink.hasV2Profile) {
						window.location.href = `${new URL(window.location.origin)}fit?engagementid=${engagementId}&documentid=${quicklink.profileV2DocumentId}`;
					} else {
						let isEngagementAccessible = false;

						dispatch(getEngagement(engagementId, isEngagementAccessible))
							.then(() => {
								let engagement = store.getState().engagement;
								if (engagement) {
									window.location.href = `${env.getURL('eng_web')}/pages/engagementprofilepage/engagementprofile.html?countryid=${
										engagement.countryId
									}&engagementid=${engagementId}&engagementdesc=${engagement.engagementDescription}&userPrefLang=${
										displayLanguages[engagement.languageId].value
									}&workspacedesc=${engagement.workspaceDescription}&workspaceid=${engagement.workspaceId}&engagementversionfull=${
										engagement.engagementVersion
									}`;
								}
							})
							.catch((result) => {
								if (result && result.response && result.response.status === 403) {
									dispatch(raiseJSValidationError(null, labels.invalidEngagementId, isMFE, false));
								}
							});
					}
				} else {
					dispatch(raiseJSValidationError(null, labels.noProfileInformationFound, isMFE, false));
				}
			}
		})
		.catch((result) => {
			if (result && result.response && result.response.status === 403) {
				dispatch(raiseJSValidationError(null, labels.noProfileInformationFound, isMFE, false));
			}
		});
}

export function initializeRedirection() {
	let engWeb = env.getURL('eng_web');
	let paths = location.pathname.split('/');

	if (paths[1].toLowerCase() === 'v1redirect') {
		let queryStringParams = queryString.parse(location.search.toLowerCase());
		const redirectedBy = '&rdFrom=clt';
		if (paths[2]?.toLowerCase() === 'engagementprofile') {
			engWeb = env.getKronosURL() + 'initprofile?engagementid=' + queryStringParams.engagementid;
			engWeb = engWeb + '&referrer=' + env.getURL('Zeus_web') + redirectedBy;
		} else if (queryStringParams.showindependence && queryStringParams.showindependence === 'true') {
			engWeb = env.getKronosURL() + 'independence?engagementid=' + queryStringParams.engagementid;
			engWeb = engWeb + '&referrer=' + env.getURL('Zeus_web') + redirectedBy;
		} else if (queryStringParams.grantarchiveaccess && queryStringParams.grantarchiveaccess === 'true') {
			// Navigate to manage team for archived engagement
			engWeb = location.origin + '/manageteam?engagementid=' + queryStringParams.engagementid;
		} else {
			engWeb = engWeb + '/pages/index/index.html' + location.search + redirectedBy;
		}
		location.href = engWeb;
	}
}

export function initializeRedirectionV1() {
	// below listener is added to fix the issue with deep routing for PAAS environments
	// TODO: Remove after all features with #routes in v1 are migrated to hercules
	const v1redirection = (event) => {
		if (event.ctrlKey || event.shiftKey) {
			return;
		}

		const a = event.target.closest('a');
		if (a && a.attributes['href']) {
			let urlVal = _.trimStart(a.attributes['href'].value, '/');
			let engWeb = env.getURL('eng_web');
			if (urlVal.split(engWeb).length > 1) {
				const svg = event.target.closest('svg');
				if (svg && svg.classList && svg.classList.length > 0 && attributeListSkipTaskOpen.includes(svg.classList[0])) {
					// Return if click on tagIcon in TaskList item
					return;
				}
				if (event.target && event.target.id && attributeListSkipTaskOpen.includes(event.target.id)) {
					// Return if click on tag count in TaskList item
					return;
				}

				if (event.target.closest('.tooltip-Menu-TasklistItem')) {
					// when user click on tooltip menu for tag and service request icon
					return;
				}

				event.preventDefault();
				const hash = new URL(urlVal).hash;
				let urlWithoutHash = urlVal.split('#')[0];
				let isTaskRoute = hash.includes('#task/');
				const openInNewTab = a.dataset.opennewtab;
				if (hash) {
					urlWithoutHash = _.trimEnd(urlWithoutHash, '&') + '&hashRoute=' + hash.split('#')[1];
				}

				if (isTaskRoute || openInNewTab) {
					window.open(urlWithoutHash, '_blank');
				} else {
					window.location.href = urlWithoutHash;
				}
			}
		}
	};

	window.removeEventListener('click', v1redirection, true);
	window.addEventListener('click', v1redirection, true);
}

export function initializeReturnURL() {
	if (window.location.search) {
		if (window.location.search.indexOf('referrer=') > 0) {
			let referrer = window.location.search.substring(window.location.search.indexOf('referrer='), window.location.search.length);
			if (referrer && referrer.length > 0) {
				referrer = referrer.toLowerCase().substring('referrer='.length, referrer.length);
				env.setReturnUrl(referrer);
			}
		}
	}
}

export function initializeTeamsIntegration(authService, config) {
	const account = authService.getActiveAccount();

	if (account) {
		initTeamsProvider(config)
			.then((provider) => {
				Providers.globalProvider = provider;
				Providers.globalProvider.setState(ProviderState.SignedIn);
			})
			.catch((error) => {
				console.log('error', error);
			});
	}
}
